import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPendingRefunds, processRefund } from "../../redux/homeAction";

export default function RefundsPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  const { refunds, processingId } = useSelector(
    (state) => state.home || { refunds: [], processingId: null },
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await dispatch(fetchPendingRefunds());
      setLoading(false);
    };
    loadData();
  }, [dispatch]);

  const handleProcessRefund = (transactionId) => {
    if (
      !window.confirm(
        "Are you sure you want to process this refund through the gateway?",
      )
    )
      return;

    dispatch(
      processRefund(transactionId, (successMessage) => {
        alert(successMessage);
      }),
    );
  };

  if (loading)
    return (
      <div className="p-6 text-center text-gray-600 font-medium">
        Loading pending refunds...
      </div>
    );

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
          Pending Refunds Management
        </h1>
        <span className="bg-indigo-100 text-indigo-800 text-xs sm:text-sm font-semibold px-3 py-1 rounded-full">
          Total Pending: {refunds.length}
        </span>
      </div>

      {refunds.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
          No pending refunds found.
        </div>
      ) : (
        <>
          {/* 💻 DESKTOP TABLE VIEW (Visible on Medium screens & up) */}
          <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order & Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shipping Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gateway Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {refunds.map((item) => {
                    const order = item.orderId || {};
                    const address = order.shippingAddress || {};
                    const user = item.userId || {};

                    return (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="font-bold text-blue-600">
                            {order.orderId || "N/A"}
                          </div>
                          <div className="font-medium text-gray-900 mt-0.5">
                            {user.name || "Guest"}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {user.phone || "N/A"}
                          </div>
                        </td>

                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                          <div className="font-medium text-gray-900">
                            {address.fullName || "N/A"} ({address.phone})
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {address.houseNumber}, {address.area},{" "}
                            {address.city}, {address.state} - {address.pincode}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 font-semibold rounded text-xs uppercase">
                            {item.gateway}
                          </span>
                          <div className="text-gray-500 text-xs mt-1 truncate max-w-[150px]">
                            ID: {item.gatewayPaymentId || "N/A"}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                          ₹{item.amount}
                          <div className="text-gray-400 text-[10px] font-normal">
                            Grand Total: ₹{order.totals?.grandTotal || 0}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 uppercase">
                            {item.status}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleProcessRefund(item._id)}
                            disabled={processingId === item._id}
                            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 transition shadow-sm text-xs font-semibold"
                          >
                            {processingId === item._id
                              ? "Processing..."
                              : "Process Refund"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* 📱 MOBILE CARD VIEW (Visible on Small screens only) */}
          <div className="grid grid-cols-1 gap-4 md:hidden">
            {refunds.map((item) => {
              const order = item.orderId || {};
              const address = order.shippingAddress || {};
              const user = item.userId || {};

              return (
                <div
                  key={item._id}
                  className="bg-white p-4 rounded-lg shadow border border-gray-200 flex flex-col gap-3"
                >
                  <div className="flex justify-between items-start border-b pb-2">
                    <div>
                      <span className="text-xs text-gray-400 uppercase font-semibold">
                        Order ID
                      </span>
                      <div className="font-bold text-blue-600 text-sm">
                        {order.orderId || "N/A"}
                      </div>
                    </div>
                    <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 uppercase">
                      {item.status}
                    </span>
                  </div>

                  <div className="text-sm">
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Customer
                    </span>
                    <div className="font-medium text-gray-900">
                      {user.name || "Guest"} ({user.phone || "N/A"})
                    </div>
                  </div>

                  <div className="text-sm">
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Shipping Address
                    </span>
                    <div className="text-xs text-gray-600">
                      {address.fullName}, {address.houseNumber}, {address.area},{" "}
                      {address.city}, {address.state} - {address.pincode}
                    </div>
                  </div>

                  <div className="flex justify-between items-center bg-gray-50 p-2.5 rounded text-sm">
                    <div>
                      <span className="text-xs text-gray-400 block uppercase">
                        Gateway:{" "}
                        <strong className="text-gray-700">
                          {item.gateway}
                        </strong>
                      </span>
                      <span className="text-xs text-gray-500 truncate max-w-[180px] block">
                        ID: {item.gatewayPaymentId || "N/A"}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-400 block uppercase">
                        Amount
                      </span>
                      <span className="font-bold text-green-600 text-base">
                        ₹{item.amount}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleProcessRefund(item._id)}
                    disabled={processingId === item._id}
                    className="w-full mt-2 bg-indigo-600 text-white py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 transition text-sm font-semibold shadow-sm"
                  >
                    {processingId === item._id
                      ? "Processing..."
                      : "Process Refund"}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
